<template>
  <div class="mini-chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

import Chart from "@/chart-setup";
import { dailyDateRange } from "@/market_insights/admin/dailyDateRange";

interface DailyPriceSeriesPoint {
  name: string;
  source: string;
  label: string;
  vintage: string | null;
  date: string;
  bid: number | null;
  ask: number | null;
  mid: number | null;
}

const CHART_COLORS = [
  "#0d6efd",
  "#dc3545",
  "#198754",
  "#fd7e14",
  "#6f42c1",
  "#20c997",
  "#d63384",
  "#6c757d",
];

const props = defineProps<{
  series: DailyPriceSeriesPoint[];
}>();

const chartCanvas = useTemplateRef<HTMLCanvasElement>("chartCanvas");
const chartInstance = ref<Chart | null>(null);

const renderChart = () => {
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }

  if (!chartCanvas.value || props.series.length === 0) {
    return;
  }

  const dataDates = [
    ...new Set(props.series.map((point) => point.date)),
  ].sort();
  // One label per calendar day so the x-axis spacing is time-proportional
  // even when some days have no price rows (spanGaps bridges the nulls)
  const labels = dailyDateRange(dataDates[0], dataDates[dataDates.length - 1]);
  const vintages = [
    ...new Set(props.series.map((point) => point.vintage ?? "-")),
  ].sort();

  const datasets = vintages.flatMap((vintage, index) => {
    const vintagePoints = props.series.filter(
      (point) => (point.vintage ?? "-") === vintage
    );
    const bidByDate = new Map(
      vintagePoints.map((point) => [point.date, point.bid])
    );
    const askByDate = new Map(
      vintagePoints.map((point) => [point.date, point.ask])
    );
    const color = CHART_COLORS[index % CHART_COLORS.length];
    const common = {
      borderColor: color,
      backgroundColor: color,
      fill: false,
      tension: 0.1,
      spanGaps: true,
      pointRadius: 0,
      pointHitRadius: 6,
      borderWidth: 1.5,
    };
    return [
      {
        ...common,
        label: `${vintage} ask`,
        data: labels.map((date) => askByDate.get(date) ?? null),
      },
      {
        ...common,
        label: `${vintage} bid`,
        data: labels.map((date) => bidByDate.get(date) ?? null),
        borderDash: [4, 3],
      },
    ];
  });

  chartInstance.value = new Chart(chartCanvas.value, {
    type: "line",
    data: { labels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            boxWidth: 12,
            font: { size: 10 },
            // One legend entry per vintage (solid = ask, dashed = bid)
            generateLabels: (chart) => {
              const labelsGenerator =
                Chart.defaults.plugins.legend.labels.generateLabels;
              return labelsGenerator(chart)
                .filter((item) => item.text?.endsWith(" ask"))
                .map((item) => ({
                  ...item,
                  text: item.text.replace(" ask", ""),
                }));
            },
          },
          // Each legend entry points at a vintage's ask dataset; its bid
          // dataset is the next index, so toggle the pair together
          onClick: (_event, legendItem, legend) => {
            const askIndex = legendItem.datasetIndex;
            if (askIndex === undefined) {
              return;
            }
            const chart = legend.chart;
            for (const index of [askIndex, askIndex + 1]) {
              chart.setDatasetVisibility(index, !chart.isDatasetVisible(index));
            }
            chart.update();
          },
        },
        tooltip: { enabled: true },
      },
      scales: {
        x: { ticks: { maxTicksLimit: 4, font: { size: 9 } } },
        y: { ticks: { font: { size: 9 } } },
      },
    },
  });
};

watch(() => props.series, renderChart);

onMounted(renderChart);

onBeforeUnmount(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy();
    chartInstance.value = null;
  }
});
</script>

<style lang="scss" scoped>
.mini-chart-container {
  position: relative;
  height: 180px;
}
</style>
